import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useLang, LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t.nav_home },
    { to: "/about", label: t.nav_about },
    { to: "/constitution", label: t.nav_constitution },
    { to: "/elections", label: t.nav_elections },
    { to: "/rnvp", label: t.nav_rnvp },
    { to: "/membership", label: t.nav_membership },
    { to: "/media", label: t.nav_media },
    { to: "/donation", label: t.nav_donation },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-emerald-950/95 backdrop-blur-md text-white shadow-lg" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src="/logo.jpeg" alt="Samata Party Logo" className="w-10 h-10 rounded-full bg-white object-contain p-0.5" />
            <div className="leading-tight">
              <div className="font-heading font-bold text-base sm:text-lg">{t.party_name}</div>
              <div className="text-[11px] text-amber-400 font-semibold tracking-wide">{t.party_state}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}-link`}
                className="px-3 py-2 text-sm font-medium rounded-md hover:bg-emerald-900 hover:text-amber-300 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <div className="flex rounded-full border border-emerald-700 overflow-hidden" data-testid="language-switcher">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}-btn`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    lang === l.code ? "bg-amber-600 text-white" : "text-emerald-100 hover:bg-emerald-900"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            {user ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/dashboard"}
                  data-testid="nav-dashboard-link"
                  className="px-4 py-2 text-sm font-semibold rounded-full bg-amber-600 hover:bg-amber-700 transition-colors"
                >
                  {user.role === "admin" ? t.nav_admin : t.nav_dashboard}
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="nav-logout-btn"
                  className="px-3 py-2 text-sm font-medium rounded-full border border-emerald-600 hover:bg-emerald-900 transition-colors"
                >
                  {t.logout}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-member-login-link"
                  className="px-4 py-2 text-sm font-semibold rounded-full bg-amber-600 hover:bg-amber-700 transition-colors"
                >
                  {t.nav_login}
                </Link>
                <Link
                  to="/admin/login"
                  data-testid="nav-admin-login-link"
                  className="p-2 rounded-full hover:bg-emerald-900 transition-colors"
                  title={t.nav_admin}
                >
                  <ShieldCheck className="w-5 h-5" />
                </Link>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2 rounded-md hover:bg-emerald-900"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-emerald-950 border-t border-emerald-800" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}-link`}
                className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-emerald-900"
              >
                {l.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-2">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-${l.code}-btn`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
                    lang === l.code ? "bg-amber-600 text-white" : "border border-emerald-700 text-emerald-100"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div className="pt-2 pb-1">
              {user ? (
                <div className="flex gap-2">
                  <Link
                    to={user.role === "admin" ? "/admin" : "/dashboard"}
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-dashboard-link"
                    className="flex-1 text-center px-4 py-2 text-sm font-semibold rounded-full bg-amber-600"
                  >
                    {user.role === "admin" ? t.nav_admin : t.nav_dashboard}
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setOpen(false); }}
                    data-testid="mobile-nav-logout-btn"
                    className="px-4 py-2 text-sm rounded-full border border-emerald-600"
                  >
                    {t.logout}
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-member-login-link"
                    className="flex-1 text-center px-4 py-2 text-sm font-semibold rounded-full bg-amber-600"
                  >
                    {t.nav_login}
                  </Link>
                  <Link
                    to="/admin/login"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-admin-login-link"
                    className="flex-1 text-center px-4 py-2 text-sm rounded-full border border-emerald-600"
                  >
                    {t.nav_admin}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
